import React, { useState, useEffect } from "react";
import Slider from "../Slider/Slider";
import Text from "../Text/Text";
import { useNavigate } from "react-router-dom";
import emailjs from "emailjs-com";
import Swal from "sweetalert2";
import Card from "../Card/Card";
import Modal from "../Modal/Modal";
import Details from "../Details/Details";
import Gallery from "../Gallery/Gallery";
import Price from "../Price/Price";
import Amenities from "../Amenities/Amenities";
import { Helmet } from "react-helmet";
import Location from "../Location/Location";

const Home = () => {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "", // Changed from query to message
    option: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");

  useEffect(() => {
    setShowModal(true);
  }, []);

  const hideModal = () => setShowModal(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
    validateField(name, value);
  };

  const validateField = (fieldName, value) => {
    let errorMessage = "";

    switch (fieldName) {
      case "name":
        if (!value.trim()) errorMessage = "Name is required";
        else if (!/^[A-Za-z\s]+$/.test(value))
          errorMessage = "Name should contain only alphabets";
        break;
      case "email":
        if (!value.trim()) errorMessage = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(value)) errorMessage = "Email is invalid";
        break;
      case "mobile":
        if (!value.trim()) errorMessage = "Mobile number is required";
        else if (!/^\d{10}$/.test(value))
          errorMessage = "Mobile number should be 10 digits";
        break;
      case "option":
        if (!value) errorMessage = "Please select an option";
        break;
      case "message": // Changed from query to message
        if (!value.trim())
          errorMessage = "Message is required"; // Changed text
        else if (value.length < 3)
          errorMessage = "Message should be at least 15 characters long"; // Changed text
        break;
      default:
        break;
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: errorMessage,
    }));
  };

  const validateForm = () => {
    let isValid = true;

    Object.entries(formValues).forEach(([fieldName, value]) => {
      validateField(fieldName, value);
      if (!value || formErrors[fieldName]) isValid = false;
    });

    return isValid;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    // Prepare template parameters including bind_message
    const templateParams = {
      ...formValues,
      bind_message: "Ace Hanei", // Added bind_message with the required value
    };

    emailjs
      .send(
        "service_cn3p6bh",
        "template_njp17vm",
        templateParams,
        "M9d0B5RLs-0It1XRj",
      )
      .then(() => {
        setSubmitting(false);
        Swal.fire("Thank You!", "Email sent successfully!", "success");
        setFormValues({
          name: "",
          email: "",
          mobile: "",
          message: "", // Changed from query to message
          option: "",
        });
        setFormErrors({});
        hideModal();
        navigate("/thankyou");
      })
      .catch(() => {
        setSubmitting(false);
        setSubmissionError("Failed to send email. Please try again later.");
        Swal.fire(
          "Error",
          "Failed to send email. Please try again later.",
          "error",
        );
      });
  };

  return (
    <>
      <Helmet>
        {/* ----------------------------------------------------
      BASIC SEO TAGS
  ---------------------------------------------------- */}
        <title>
          ACE Hanei Sector 12 Noida Extension | 3.5, 4.5 & 4.5-XL BHK Luxury
          Apartments
        </title>
        <meta
          name="description"
          content="ACE Hanei at Sector 12 Noida Extension offers premium 3.5, 4.5 & 4.5-XL BHK luxury apartments with triple-height lobby, 4 lifts per floor, clubhouse, swimming pool, gym, and more. Book EOI at effective price ₹14,000 per sq ft."
        />
        <meta
          name="keywords"
          content="ACE Hanei, ACE Hanei Sector 12 Noida Extension, ACE Hanei Noida Extension, luxury apartments Noida Extension, best residential project in Greater Noida West, affordable apartments in Noida Extension, luxury apartments in Noida Extension, 3.5 BHK Noida Extension, 4.5 BHK Noida Extension, ACE Group projects, ACE Hanei price list, ACE Hanei brochure, ACE Hanei amenities, new residential projects in Greater Noida West, premium flats in Noida Extension, upcoming projects in Noida Extension, high-rise apartments in Greater Noida West, flats in Greater Noida West"
        />
        <link rel="canonical" href="https://acegroupsales.com/" />
        <meta name="robots" content="index, follow" />
        <meta name="theme-color" content="#ffffff" />

        {/* ----------------------------------------------------
      OPEN GRAPH TAGS
  ---------------------------------------------------- */}
        <meta
          property="og:title"
          content="ACE Hanei - Luxury 3.5, 4.5 & 4.5-XL BHK Apartments in Noida Extension"
        />
        <meta
          property="og:description"
          content="Explore ACE Hanei at Sector 12 Noida Extension — Premium 3.5, 4.5 & 4.5-XL BHK luxury residences with world-class amenities & modern lifestyle. Effective price ₹14,000 per sq ft."
        />
        <meta
          property="og:image"
          content="https://acegroupsales.com/poster.webp"
        />
        <meta property="og:url" content="https://acegroupsales.com/" />
        <meta property="og:type" content="website" />

        {/* ----------------------------------------------------
      TWITTER META TAGS
  ---------------------------------------------------- */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="ACE Hanei – Luxury 3.5, 4.5 & 4.5-XL BHK Flats in Sector 12 Noida Extension"
        />
        <meta
          name="twitter:description"
          content="Premium 3.5, 4.5 & 4.5-XL BHK apartments with world-class amenities at ACE Hanei, Sector 12 Noida Extension. Starting ₹14,000 per sq ft."
        />
        <meta
          name="twitter:image"
          content="https://acegroupsales.com/poster.webp"
        />

        {/* ----------------------------------------------------
      JSON-LD SCHEMA
  ---------------------------------------------------- */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "Organization",
                  name: "ACE Group",
                  url: "https://acegroupsales.com/",
                  logo: "https://acegroupsales.com/1.webp",
                  sameAs: ["https://acegroupsales.com/"],
                  contactPoint: [
                    {
                      "@type": "ContactPoint",
                      telephone: "+91 7303734500",
                      contactType: "Sales",
                      areaServed: "IN",
                      availableLanguage: ["English", "Hindi"],
                    },
                  ],
                  founder: "ACE Group",
                  description:
                    "ACE Group is a premium real estate developer offering luxury residential projects like ACE Hanei in Noida Extension with world-class amenities and excellent connectivity.",
                },
                {
                  "@type": "WebSite",
                  name: "ACE Group Sales",
                  url: "https://acegroupsales.com/",
                  publisher: {
                    "@type": "Organization",
                    name: "ACE Group",
                    logo: {
                      "@type": "ImageObject",
                      url: "https://acegroupsales.com/1.webp",
                    },
                  },
                  potentialAction: {
                    "@type": "SearchAction",
                    target: "https://acegroupsales.com/?q={search_term_string}",
                    "query-input": "required name=search_term_string",
                  },
                },
                {
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    {
                      "@type": "ListItem",
                      position: 1,
                      name: "Home",
                      item: "https://acegroupsales.com/",
                    },
                  ],
                },
                {
                  "@type": "ApartmentComplex",
                  name: "ACE Hanei",
                  url: "https://acegroupsales.com/",
                  description:
                    "ACE Hanei is a premium residential project offering ultra-luxury 3.5, 4.5 & 4.5-XL BHK apartments at Sector 12 Noida Extension. Total land: 6.5 acres, 6 towers, G+25 floors, triple-height lobby, 4 lifts per floor, clubhouse, swimming pool, gym, tennis court, badminton court, jogging track, children's play area, and eco-friendly features.",
                  slogan:
                    "3.5, 4.5 & 4.5-XL BHK Luxury Apartments in Noida Extension",
                  image: [
                    "https://acegroupsales.com/poster.webp",
                    "https://acegroupsales.com/a1.webp",
                    "https://acegroupsales.com/a3.webp",
                    "https://acegroupsales.com/1.webp",
                  ],
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "Sector 12",
                    addressLocality: "Noida Extension, Greater Noida West",
                    addressRegion: "Uttar Pradesh",
                    postalCode: "201308",
                    addressCountry: "IN",
                  },
                  telephone: "+91 7303734500",
                  numberOfBuildings: 6,
                  floorCount: 25,
                  petsAllowed: true,
                  tourBookingPage: "https://acegroupsales.com/",
                  amenityFeature: [
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Clubhouse",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Swimming Pool",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Fitness Center",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Tennis Courts",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Badminton Courts",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Jogging Tracks",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Children's Play Area",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Landscaped Gardens",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "24/7 Security",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "CCTV Surveillance",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Rainwater Harvesting",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Triple Height Lobby",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "4 Lifts per Tower",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Eco-Friendly Living",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Gated Community",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Interactive Fountains",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Modular Kitchen",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Italian Flooring",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Air Conditioners",
                    },
                    {
                      "@type": "LocationFeatureSpecification",
                      name: "Wardrobes",
                    },
                  ],
                  geo: {
                    "@type": "GeoCoordinates",
                    latitude: "28.4744",
                    longitude: "77.5040",
                  },
                  additionalProperty: [
                    {
                      "@type": "PropertyValue",
                      name: "RERA Number",
                      value: "UPRERAPRJ677887",
                    },
                    {
                      "@type": "PropertyValue",
                      name: "Land Area",
                      value: "6.5 Acres",
                    },
                    {
                      "@type": "PropertyValue",
                      name: "Total Towers",
                      value: "6",
                    },
                    {
                      "@type": "PropertyValue",
                      name: "Total Floors",
                      value: "G+25",
                    },
                    {
                      "@type": "PropertyValue",
                      name: "Lifts per Tower",
                      value: "4",
                    },
                    {
                      "@type": "PropertyValue",
                      name: "Construction Type",
                      value: "Mivan Constructions",
                    },
                  ],
                },
                {
                  "@type": "OfferCatalog",
                  name: "ACE Hanei Price List",
                  url: "https://acegroupsales.com/",
                  itemListElement: [
                    {
                      "@type": "Offer",
                      name: "3.5 BHK (TYPE-1) 2290 sq.ft",
                      price: "On Request",
                      priceCurrency: "INR",
                      description: "Area: 2290 SQ.FT. (Mivan Constructions)",
                    },
                    {
                      "@type": "Offer",
                      name: "4.5 BHK (TYPE-2) 3200 sq.ft",
                      price: "On Request",
                      priceCurrency: "INR",
                      description: "Area: 3200 SQ.FT. (Mivan Constructions)",
                    },
                    {
                      "@type": "Offer",
                      name: "4.5-XL BHK (TYPE-3) 4190 sq.ft",
                      price: "On Request",
                      priceCurrency: "INR",
                      description: "Area: 4190 SQ.FT. (Mivan Constructions)",
                    },
                  ],
                },
                {
                  "@type": "ContactPage",
                  url: "https://acegroupsales.com/",
                  name: "ACE Hanei Sales Inquiry",
                  telephone: "+91 7303734500",
                  contactType: "Sales Inquiry",
                  description:
                    "Contact ACE Hanei for booking, price details at ₹14,000 per sq ft, brochure download, site visit and EOI booking.",
                  provider: {
                    "@type": "RealEstateAgent",
                    name: "Authorized Channel Partner - Shishir",
                    identifier: "UP RERA Registration No. UPRERAAGT24002",
                  },
                },
                {
                  "@type": "ImageGallery",
                  name: "ACE Hanei Image Gallery",
                  url: "https://acegroupsales.com/",
                  description:
                    "Gallery showcasing images of ACE Hanei Sector 12 project",
                  image: [
                    "https://acegroupsales.com/a1.webp",
                    "https://acegroupsales.com/a3.webp",
                    "https://acegroupsales.com/poster.webp",
                  ],
                },
              ],
            }),
          }}
        />
      </Helmet>
      <Modal show={showModal} handleClose={hideModal}>
        <h2 className="modal-heading">Get More Details</h2>
        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formValues.name}
              onChange={handleChange}
              className={`input ${formErrors.name ? "invalid" : ""}`}
            />
            {formErrors.name && (
              <span className="error">{formErrors.name}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formValues.email}
              onChange={handleChange}
              className={`input ${formErrors.email ? "invalid" : ""}`}
            />
            {formErrors.email && (
              <span className="error">{formErrors.email}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="mobile">Mobile:</label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              required
              value={formValues.mobile}
              onChange={handleChange}
              className={`input ${formErrors.mobile ? "invalid" : ""}`}
            />
            {formErrors.mobile && (
              <span className="error">{formErrors.mobile}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="option">Select Flat Option:</label>
            <select
              id="option"
              name="option"
              required
              value={formValues.option}
              onChange={handleChange}
              className={`input ${formErrors.option ? "invalid" : ""}`}
            >
              <option value="">-Select-</option>
              <option value="4.5-XL BHK(4190 SQ. FT.)">
                4.5-XL BHK(4190 SQ. FT.)
              </option>
              <option value="4.5 BHK(3200 SQ. FT.)">
                4.5 BHK(3200 SQ. FT.)
              </option>
              <option value="3.5 BHK(2290 SQ. FT.)">
                3.5 BHK(2290 SQ. FT.)
              </option>
            </select>
            {formErrors.option && (
              <span className="error">{formErrors.option}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="message">Your Message:</label>{" "}
            {/* Changed label from Query to Message */}
            <textarea
              id="message"
              name="message"
              required
              value={formValues.message}
              onChange={handleChange}
              className={`input ${formErrors.message ? "invalid" : ""}`}
              rows="4"
            />
            {formErrors.message && (
              <span className="error">{formErrors.message}</span>
            )}{" "}
            {/* Updated from formErrors.query */}
          </div>
          <div className="butt-f">
            <button className="button-3" type="submit" disabled={submitting}>
              {submitting ? "Submitting..." : "SUBMIT"}
            </button>
          </div>
          {submissionError && (
            <p className="submission-error">{submissionError}</p>
          )}
        </form>
      </Modal>

      <Slider />
      <Text />
      <Card />
      <Details />
      <Gallery />
      <Price />
      <Amenities />
      <Location />
    </>
  );
};

export default Home;
